// BONUS-ADMIN-EINFACH (Owner 30.10.2026) — Zeitfenster der zentralen Bonusangebote.
// Das Backend speichert ein Fenster als { days, from, to }: `days` sind die STARTTAGE (1 = Mo … 7 = So),
// `to < from` endet am Folgetag (So → Mo). Diese Datei rechnet nur für die Anzeige und die
// Vorabprüfung; maßgeblich bleibt die Serverprüfung (bonusCampaigns.validate).
export const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
export const DAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
export const WEEK_MINUTES = 7 * 1440;
export const MAX_WINDOWS = 12;
const TIME = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export const minute = (value) => (TIME.test(String(value || '')) ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3)) : null);
export const clock = (minutes) => {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};
const nextDay = (day) => (day % 7) + 1;

// Eine Eingabezeile: Start (Tag + Uhrzeit) → Ende (gleicher Tag oder Folgetag + Uhrzeit),
// optional an weiteren Starttagen wiederholt.
export const emptyRow = (startDay = 1) => ({ startDay, from: '', endDay: 'same', to: '', repeat: [] });
export const rowDays = (row) => [...new Set([Number(row.startDay), ...(row.repeat || []).map(Number)])].sort((a, b) => a - b);

// Beim Tippen der Uhrzeiten folgt der Endtag den Zeiten: ein früheres Ende liegt am Folgetag.
export function withTimes(row, change) {
  const next = { ...row, ...change };
  const from = minute(next.from), to = minute(next.to);
  if (from !== null && to !== null && from !== to) next.endDay = to < from ? 'next' : 'same';
  return next;
}

/** Feldfehler einer Zeile oder null. */
export function rowError(row) {
  const from = minute(row.from), to = minute(row.to);
  if (from === null) return 'Startzeit fehlt.';
  if (to === null) return 'Endzeit fehlt.';
  if (from === to) return 'Start und Ende dürfen nicht gleich sein (höchstens 23:59 Stunden).';
  if (row.endDay === 'same' && to < from) return 'Ende liegt vor dem Start. Für ein Ende nach Mitternacht „Folgetag“ wählen.';
  if (row.endDay === 'next' && to > from) return 'Ein Fenster darf höchstens 24 Stunden dauern. Für längere Zeiten ein weiteres Fenster anlegen.';
  return null;
}

export const rowToWindow = (row) => ({ from: row.from, to: row.to, days: rowDays(row) });
export function windowToRow(w) {
  const days = Array.isArray(w.days) && w.days.length ? [...w.days].sort((a, b) => a - b) : [1, 2, 3, 4, 5, 6, 7];
  return { startDay: days[0], from: w.from, to: w.to, endDay: w.from > w.to ? 'next' : 'same', repeat: days.slice(1) };
}

/** Intervalle [start, ende) in Wochenminuten ab Mo 00:00; über So 24:00 hinaus geteilt. */
export function windowIntervals(w) {
  const from = minute(w.from), to = minute(w.to);
  if (from === null || to === null || from === to) return [];
  const days = Array.isArray(w.days) && w.days.length ? w.days : [1, 2, 3, 4, 5, 6, 7];
  return days.flatMap((day) => {
    const start = (day - 1) * 1440 + from, end = start + (to > from ? to - from : 1440 - from + to);
    return end > WEEK_MINUTES ? [[start, WEEK_MINUTES], [0, end - WEEK_MINUTES]] : [[start, end]];
  });
}

/** Schnittmengen zweier Intervalllisten, zusammengefasst. */
export function intersections(a, b) {
  const out = [];
  for (const [s1, e1] of a) for (const [s2, e2] of b) {
    const s = Math.max(s1, s2), e = Math.min(e1, e2);
    if (s < e) out.push([s, e]);
  }
  out.sort((x, y) => x[0] - y[0]);
  return out.reduce((merged, item) => {
    const last = merged[merged.length - 1];
    if (last && item[0] <= last[1]) last[1] = Math.max(last[1], item[1]); else merged.push([...item]);
    return merged;
  }, []);
}

/** Zeitpunkt in Wochenminuten als „Mi 23:00“; das Ende 24:00 bleibt am selben Tag. */
export function weekPoint(minutes, { end = false } = {}) {
  const m = ((minutes % WEEK_MINUTES) + WEEK_MINUTES) % WEEK_MINUTES;
  if (end && m % 1440 === 0) {
    const day = (Math.floor(m / 1440) + 6) % 7;
    return `${DAYS[day]} 24:00`;
  }
  return `${DAYS[Math.floor(m / 1440)]} ${clock(m)}`;
}
export const intervalText = ([start, end]) => `${weekPoint(start)}–${weekPoint(end, { end: true })}`;

// Gleicher Wortlaut wie der Fahrer-Push (backend/src/lib/bonusCentralNotices.js describeWindow).
export function dayList(days) {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  if (sorted.length === 7) return 'täglich';
  const runs = [];
  for (const d of sorted) { const last = runs[runs.length - 1]; if (last && d === last[1] + 1) last[1] = d; else runs.push([d, d]); }
  return runs.map(([a, b]) => (b - a >= 2 ? `${DAYS[a - 1]}–${DAYS[b - 1]}` : a === b ? DAYS[a - 1] : `${DAYS[a - 1]}, ${DAYS[b - 1]}`)).join(', ');
}
export function describeWindow(w) {
  const days = Array.isArray(w.days) && w.days.length ? w.days : [1, 2, 3, 4, 5, 6, 7];
  if (w.from < w.to) return `${dayList(days)} ${w.from}–${w.to}`;
  if (w.to === '00:00') return `${dayList(days)} ${w.from}–24:00`;
  if (days.length <= 3) return [...days].sort((a, b) => a - b).map((d) => `${DAYS[d - 1]} ${w.from}–${DAYS[nextDay(d) - 1]} ${w.to}`).join(', ');
  return `${dayList(days)} jeweils ${w.from}–${w.to} (Ende am Folgetag)`;
}
export const describeWindows = (windows) => (windows || []).map(describeWindow).join('; ');
export const durationText = (row) => {
  const from = minute(row.from), to = minute(row.to);
  if (from === null || to === null || from === to) return '';
  const total = to > from ? to - from : 1440 - from + to, h = Math.floor(total / 60), m = total % 60;
  return m ? `${h} Std. ${m} Min.` : `${h} Std.`;
};

/** Überschneidungen innerhalb des Entwurfs (der Server lehnt sie ab). */
export function internalOverlaps(windows) {
  const out = [];
  windows.forEach((w, i) => windows.slice(i + 1).forEach((other, k) => {
    const hit = intersections(windowIntervals(w), windowIntervals(other));
    if (hit.length) out.push({ first: i, second: i + k + 1, intervals: hit });
  }));
  return out;
}

/** Überschneidungen des Entwurfs mit vorhandenen (nicht beendeten) Zeitbonus-Angeboten derselben Partner. */
export function offerConflicts(windows, partnerIds, offers) {
  const mine = windows.flatMap(windowIntervals), wanted = new Set(partnerIds);
  return (offers || []).flatMap((offer) => {
    const terms = offer.terms || {};
    if (!(terms.per_ride_cents > 0) || !Array.isArray(terms.windows)) return [];
    if (!(offer.partners || []).some((p) => wanted.has(p.partner_id))) return [];
    const hit = intersections(mine, terms.windows.flatMap(windowIntervals));
    return hit.length ? [{ offer_id: offer.id, title: terms.title, enabled: !!offer.enabled, intervals: hit }] : [];
  });
}

// Vorlagen: nur Aufbau (Art, Bezeichnung, Zeitfenster) — Beträge und Limits bleiben bewusst leer.
export const TEMPLATES = Object.freeze([
  { id: 'night-wed', label: 'Nacht Mi 23:00–Do 04:00', mode: 'ride', title: 'Nachtbonus Mi–Do',
    rows: [{ startDay: 3, from: '23:00', endDay: 'next', to: '04:00', repeat: [] }] },
  { id: 'weekend-nights', label: 'Wochenend-Nächte Fr+Sa 22:00–04:00', mode: 'ride', title: 'Wochenend-Nächte',
    rows: [{ startDay: 5, from: '22:00', endDay: 'next', to: '04:00', repeat: [6] }] },
  { id: 'weekend-day', label: 'Wochenende Sa+So 10:00–22:00', mode: 'ride', title: 'Wochenende tagsüber',
    rows: [{ startDay: 6, from: '10:00', endDay: 'same', to: '22:00', repeat: [7] }] },
  { id: 'rush-hour', label: 'Berufsverkehr Mo–Fr 07–09 und 16–19', mode: 'ride', title: 'Berufsverkehr',
    rows: [{ startDay: 1, from: '07:00', endDay: 'same', to: '09:00', repeat: [2, 3, 4, 5] },
      { startDay: 1, from: '16:00', endDay: 'same', to: '19:00', repeat: [2, 3, 4, 5] }] },
  { id: 'weekly-goal', label: 'Wochenziel (ganze Woche)', mode: 'goal', title: 'Wochenziel', rows: [] },
]);

// Serverfehler → Feld + Text. Die Codes kommen unverändert vom Server (bonusCampaigns.validate).
export const SERVER_ERRORS = Object.freeze({
  bonus_campaign_windows_overlap: ['windows', 'Die Zeitfenster überschneiden sich – auch über Mitternacht. Zeiten oder Tage anpassen.'],
  bonus_campaign_windows_invalid: ['windows', 'Gültige Uhrzeiten angeben; Start und Ende dürfen nicht gleich sein.'],
  bonus_campaign_days_invalid: ['windows', 'Für jedes Zeitfenster einen gültigen Starttag wählen; jeder Tag nur einmal.'],
  bonus_campaign_window_required: ['windows', 'Mindestens ein Zeitfenster angeben.'],
  bonus_campaign_amount_required: ['amounts', 'Einen positiven Fahrtbonus oder ein Wochenziel mit Fahrten und Betrag angeben.'],
  bonus_campaign_week_invalid: ['week_start', 'Diese oder die nächste VOOGO-Woche wählen. Bitte neu laden.'],
  bonus_campaign_goal_week_invalid: ['week_start', 'Zeitbonus und Wochenziel müssen in derselben Woche beginnen.'],
  bonus_central_partner_missing: ['partner_id', 'Dieser Partner ist nicht freigegeben oder nicht mehr verfügbar.'],
  bonus_campaign_invalid: ['form', 'Eingaben außerhalb der erlaubten Werte (Bezeichnung bis 80 Zeichen, Beträge, Fahrtenzahl, Limit).'],
  bonus_central_invalid: ['form', 'Eingaben unvollständig. Bitte die markierten Felder prüfen.'],
});
export const serverError = (code) => SERVER_ERRORS[code] || null;
